import { Paginated, Query } from "@feathersjs/feathers";
import { action, computed, makeObservable, observable, reaction, runInAction, when } from "mobx";
import { RootStore, BaseDataStore } from ".";
import { Log } from "../models";
import { DataType, ILog, LoadingStatus, LogType, RoomLogProps, SpaceType } from "../types";
import { DataContainer } from "./dataContainer";
import { paramsForServer } from "feathers-hooks-common";
import { isBefore } from "date-fns";
import toast from "react-hot-toast";
import i18n from "src/i18n";
import i18next from "src/i18n";
import { LogParser } from "src/views/log/logParser";
import { GenerateRoomLogFileExportRequest } from "src/types/customServiceTypes";
import { jsPDF } from "jspdf";

const emptyFilter: Record<RoomLogProps, (string | number)[]> = {
  "number":        null,
  "from":          null,
  "to":            null,
  "title":         null,
  "created_at":    null,
  "status":        null,
  "date":          null,
  "start_date":    null,
  "delay_date":    null,
  "user":          null,
  "action":        null,
}

export class LogStore extends BaseDataStore<Log, ILog> {
  private readonly newsFeedPageSize:       number = 10;
  private readonly meetingSummaryPageSize: number = 10;
  readonly         newsItemEntryPageSize:  number = 10;
  readonly         roomLogPageSize:        number = 10;
  meetingSummary: DataContainer<ILog, Log> = null;
  news:           DataContainer<ILog, Log> = null;
  logItemEntries: DataContainer<ILog, Log> = null;
  roomLog:        DataContainer<ILog, Log> = null;
  roomLogFilter:  Record<RoomLogProps, any> = emptyFilter;
  activeLogItem:  number = null;
  last_logs:     {[item_id: number]: Log} = {};
  logParser:       LogParser = new LogParser();

  constructor(readonly rootStore: RootStore) {
    super(rootStore, Log.create, DataType.Log, false);

    makeObservable(this, {
      activeLogItem:     observable,
      roomLogFilter:     observable,
      last_logs:         observable,

      isEmptyRoomLogFilter: computed,
      getLastLogs:          computed,

      setActiveLogItem:   action,
      setNewsFeedFilter:  action,
      setRoomLogFilter:   action,
      resetRoomLogFilter: action,
      updateRoomLogQuery: action,
    });
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();

    // Load initial log for question
    reaction(() => this.rootStore.getActiveItem, () => {
        if (!this.rootStore.getActiveItem?.id) {
          this.container = {};
          this.meetingSummary.clear();
        }
        else {
          this.loadItems(this.getCurFilter());
          if(this.rootStore.getActiveItem.data_type === DataType.Meeting) {
            this.meetingSummary.setQueryParams({meeting_id: this.rootStore.getActiveItem.id});
            if(this.meetingSummary.getStatus === LoadingStatus.None) this.meetingSummary.load();
          }
        }
    }, { name: "log_onActive_load" });
    reaction(() => !this.rootStore.authStore.getCurRoomID, () => {
        this.container = {};
    }, {name: "log_onRoom_unset" });

    this.setupStorage();

    // Since only entries can only ever be created, the event hook chain is not needed here.
    // Instead, only the created event is setup manually
    this.service.on("created", (data: ILog) => {
      runInAction(() => this.container[data.id] = Log.create(data));
      this.logItemEntries.update(data, "created");
      this.news.update(data, "created");

      // Update last log entries if the corresponding item is currently tracked
      if(data.item_id && this.last_logs[data.item_id]) this.last_logs[data.item_id] = Log.create(data);

      // If its a log entry for entering or leaving a room
      if(data.data_type === DataType.User && this.rootStore.meetingStore.attendees) {
        runInAction(() => {
          this.rootStore.meetingStore.attendees[data.user_id] = this.rootStore.meetingStore.attendees[data.user_id] || {checked_in: false};
          if(data.event_type === "enter" && new Date(data.created_at) >= (this.rootStore.meetingStore.attendees[data.user_id].in || null)) 
            this.rootStore.meetingStore.attendees[data.user_id].in  = new Date(data.created_at);
          if(data.event_type === "leave" && new Date(data.created_at) >= (this.rootStore.meetingStore.attendees[data.user_id].out || null)) 
            this.rootStore.meetingStore.attendees[data.user_id].out = new Date(data.created_at);
          this.rootStore.meetingStore.attendees[data.user_id].checked_in = (this.rootStore.meetingStore.attendees[data.user_id].in || null) > (this.rootStore.meetingStore.attendees[data.user_id].out || null);
        });
      }

      // If the user is on the dashboard, no toaster is needed
      if(!this.rootStore.authStore.getCurRoomID) return;

      // Check user settings if the log entry should create a news feed entry
      // if(data.item && this.rootStore.authStore.getCurUser.isAffected(data, data.item))
      //   toast.success(i18n.t("updated_newsfeed"));
    });
  }

  protected setupStorage() {
    // Meeting Summary log entries:
    this.meetingSummary = new DataContainer<ILog, Log>(
      (params: {meeting_id: number}) => paramsForServer({
        noLinks: true,
        query: {
          "log.meeting_id": params.meeting_id,
        }
      }),
      this.service,
      {
        paginate:    true,
        sortProp:    "created_at",
        limited:     false,
        sort:        -1,
        pageSize:    this.meetingSummaryPageSize,
        conversion:  Log.create,
        eventFilter: (data:ILog) => Number(data.meeting_id) === this.rootStore.getActiveItem?.id,
        name: "meeting.summary"
      }
    );

    // News Feed parent entries
    this.news = new DataContainer<ILog, Log>(
      (params: {room_ids?: number[], start?: Date, end?: Date}) => ({
        room_id: {
          $in: params.room_ids,
        },
        created_at: {
          $gte: params.start?.toISOString(),
          $lte: params.end?.toISOString(),
        }
      }),
      this.service,
      {
        paginate:    true,
        limited:     false,
        pageSize:    this.newsFeedPageSize,
        sortProp:    "created_at",
        conversion:  Log.create,
        eventFilter: (data: ILog , params?: {[key: string]: any}) => {
          if(!params || data.data_type === DataType.User) return false;
          const isValid = (!params.room_ids || params.room_ids.includes(data.room_id))
            && (!params.start || isBefore(params.start, new Date(data.created_at)))
            && (!params.end || isBefore(new Date(data.created_at), params.end));
          if(!isValid) return false;
          return !(this.news.getArray.find(i => i.id === data.item_id));
        },
        name:        "news.feed",
      }
    );

    reaction(() => this.news.getArray, async () => {
      const _ll: {[item_id: number]: Log} = {};
      const item_ids: number[] = this.news.getArray.map(l => l.item_id);

      for(const item_id of item_ids) {
        const _l: Paginated<ILog> = await this.service.find(paramsForServer({
          noLinks: true,
          query: {
            $limit:1,
            $sort: { created_at: -1 },
            item_id,
          }
        }));
        if(_l.total) _ll[item_id] = Log.create(_l.data[0]);
      }

      runInAction(() => this.last_logs = _ll);
    });

    // News Feed Child entries
    this.logItemEntries = new DataContainer<ILog, Log>(
      (params: {item_id: number, room_id: number}) => paramsForServer({
        noLinks: true,
        query: {
          "log.item_id": params.item_id,
          "log.room_id": params.room_id,
          $sort: { "created_at": -1 }
        }
      }),
      this.service,
      {
        paginate:    true,
        limited:     false,
        extendPage:  false,
        pageSize:    this.newsItemEntryPageSize,
        sortProp:    "created_at",
        sort:        -1,
        conversion:  Log.create,
        eventFilter: (data: ILog) => data.item_id && Number(data.item_id) === Number(this.activeLogItem),
        params:      null,
        name:        "news.entries",
      }
    );

    // Refresh containers when workspace is changed
    reaction(() => this.rootStore.authStore.getCurWorkspaceID, () => {
      if(this.rootStore.authStore.getCurWorkspaceID) {
        this.news.clear();
        this.news.load();
        this.logItemEntries.clear();
        this.logItemEntries.load();
      }
    });

    // Room Log container
    this.roomLog = new DataContainer<ILog, Log>(
      "getSpaceLog",
      this.service,
      {
        paginate:    true,
        limited:     false,
        pageSize:    this.roomLogPageSize,
        extendPage:  false,
        sortProp:    "created_at",
        conversion:  Log.create,
        eventFilter: (data: ILog) => false,
        params:      null,
        name:        "log.room",
      }
    );
    reaction(() => this.roomLogFilter, this.updateRoomLogQuery);
  }

  protected getCurFilter = (): Query => paramsForServer({
    noLinks: true,
    query: {
      "log.item_id": this.rootStore.getActiveItem?.id,
      $sort: {
        'log.created_at': -1
      }
    }
  });

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  get getItems(): Log[] {
    return Object.values(this.container);
  }

  get isEmptyRoomLogFilter(): boolean {
    return !Object.values(this.roomLogFilter).find(f => Boolean(f));
  }

  get getLastLogs(): {[item_id: number]: Log} {
    return this.last_logs;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  /**
   * Set active item in the news feed. This will retrieve further log information for that
   * item. 
   * The @room_id needs to be provided to differentiate between log entries for the same item 
   * that was moved between different rooms. 
   */
  setActiveLogItem = (item_id: number, room_id: number) => {
    this.activeLogItem = Number(item_id);
    this.logItemEntries.setQueryParams((item_id && room_id) ? {item_id, room_id} : null);
    item_id ? this.logItemEntries.load() : this.logItemEntries.clear();
  }

  /**
   * Set news feed filters. The feed can be filtered by space and by (possibly 
   * open ended) time interval.
   */
  setNewsFeedFilter = (room_list?: number[], start?: Date, end?: Date) => {
    if(!(room_list || start || end)) this.news.setQueryParams(null);
    else this.news.setQueryParams({room_list, start, end })
  }

  /**
   * 
   */
  setRoomLogFilter = (prop: RoomLogProps, value: any) => {
    let val;
    if(!value) val = null;
    else val = value || null ;

    if(this.roomLogFilter[prop] === val) return;

    this.roomLogFilter[prop] = val;
    this.roomLogFilter = Object.assign({}, this.roomLogFilter);
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  resetRoomLogFilter = () => {
    this.roomLogFilter = emptyFilter;
  }
  updateRoomLogQuery = () => {
    const filter: Partial<Record<RoomLogProps, (string | number)[]>> = 
      Object.keys(this.roomLogFilter)
        .filter((k: RoomLogProps) => !!this.roomLogFilter[k])
        .reduce((res: Partial<Record<RoomLogProps, (string | number)[]>>, val: RoomLogProps) => {
          res[val] = this.roomLogFilter[val]
          return res;
        }, {});

    const log_type: LogType = this.rootStore.routingStore.pathName.split("/").pop() as LogType;

    this.roomLog.setQueryParams({
      space_id:   this.rootStore.authStore.getCurRoomID,
      space_type: SpaceType.Room,
      log_type,
      filter,
    });
  }

  loadRoomLogs = (log_type: LogType) => {
    if(!this.rootStore.authStore.getCurRoomID) {
      when(() => !!this.rootStore.authStore.getCurRoomID, () => {
        this.loadRoomLogs(log_type);
      });
      return;
    }
    
    this.roomLog.setQueryParams({
      space_id:   this.rootStore.authStore.getCurRoomID,
      space_type: SpaceType.Room,
      log_type,
    });
    if(this.roomLog.status === LoadingStatus.None) {
      this.roomLog.clear();
      this.roomLog.load();
    }
  }

  /**
   * Requests a file export of the log summary of meeting @meeing_id
   */
  generateMeetingSummary = async (meeting_id: number, file_type: "pdf" | "xlsx"): Promise<void> => {
    try {
      const res  = await this.service.generateMeetingSummary({meeting_id, file_type});
      const blob = new Blob([res], { type: `application/${file_type}` });

      if(!res || !blob) throw new Error("");

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `meeting_summary.${file_type}`;
      link.click();
    } catch (err) {
      console.error("Failed to generate meeting summary", err)
      toast.error(i18next.t("export_failed"));
      return;
    }
  }

  /**
   * Requests a file export of the room log matching @req
   */
  generateRoomLogFileExport = async (log_type: LogType, file_type: 'pdf' | 'xlsx'): Promise<boolean> => {
    
    const filter: Partial<Record<RoomLogProps, (string | number)[]>> = 
      Object.keys(this.roomLogFilter)
        .filter((k: RoomLogProps) => !!this.roomLogFilter[k])
        .reduce((res: Partial<Record<RoomLogProps, (string | number)[]>>, val: RoomLogProps) => {
          res[val] = this.roomLogFilter[val]
          return res; 
        }, {});

    const req: GenerateRoomLogFileExportRequest = {
      file_type,
      req: {
        space_id:   this.rootStore.authStore.getCurRoomID,
        space_type: SpaceType.Room,
        log_type,
        filter,
      }
    }
    let res;
    try {
      res = await this.service.generateRoomLogFileExport(req);
    } catch(err) {
      console.error("Failed to generate log file", err);
      toast.error(i18next.t("export_failed"));
      return false;
    }

    if(!res) {
      toast.error(i18next.t("export_failed"));
      return false;
    }
    if( req.file_type == 'pdf' ) {
      // Default export is a4 paper, portrait, using millimeters for units
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: 'a4',
        putOnlyUsedFonts:true
       });
      
      pdf.html(res, {
        callback: doc => {
          res = doc.output('blob');
          const blob = new Blob([res], { type: `application/${req.file_type}` });
          const link = document.createElement('a');
          link.href  = window.URL.createObjectURL(blob);
          link.download = `room_log.${req.file_type}`;
          link.click();
        }
     });

    } else {

      const blob = new Blob([res], { type: `application/${req.file_type}` });

      if(!blob) {
        toast.error(i18next.t("export_failed"));
        return false;
      }
  
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `room_log.${req.file_type}`;
      link.click();
      return true;
    }
  }
}